import { API } from "../api.js";
import { mountHome } from "../homes/index.js";
import { wireCommands } from "./commands.js";
import { populateOnboardingDropdowns, startNewGame } from "./onboarding.js";
import { $, esc, fmtMoney } from "../ui/format.js";
import { toast } from "../ui/modals.js";
import { renderCompetitors } from "../views/core/competitors.js";
import { renderConsulting } from "../views/core/consulting.js";
import { renderFinancials, renderRatios } from "../views/core/financials.js";
import { renderGlobalFootprint } from "../views/core/footprint.js";
import { renderFX } from "../views/core/fx.js";
import { renderMacro } from "../views/core/macro.js";
import { renderPublicMarkets } from "../views/core/markets.js";
import { renderNews } from "../views/core/news.js";
import { renderRisk } from "../views/core/risk.js";
import { mountSoundControl, setAmbientGroup } from "./audio.js";

let currentTab = "overview";

let snapshotHistory = [];

let optionsCache = null;

// ---------------------------------------------------------------- boot

async function boot() {
  optionsCache = await (await fetch(`${API}/options`)).json();
  window.__optionsCountries = optionsCache.countries;
  populateOnboardingDropdowns();

  // Only "no company" (404) means onboarding. A network failure or an error
  // while drawing the dashboard must never look like a lost save: the
  // onboarding screen offers "Start new game", which deletes the company.
  let res;
  try {
    res = await fetch(`${API}/company`);
  } catch (e) {
    showBootProblem("Can't reach the game server. Is it still running?", e);
    return;
  }
  if (res.ok) {
    $("onboarding-screen").classList.add("hidden");
    $("gameover-screen").classList.add("hidden");
    try {
      await enterDashboard();
    } catch (e) {
      showBootProblem("Your company is safe, but the dashboard hit an error while loading.", e);
    }
    return;
  }
  if (res.status !== 404) {
    showBootProblem(`The game server answered ${res.status}. Your save hasn't been touched.`, null);
    return;
  }
  $("dashboard-screen").classList.add("hidden");
  $("gameover-screen").classList.add("hidden");
  $("onboarding-screen").classList.remove("hidden");
}

/** A boot failure that isn't "no company": say so, keep the save, offer a retry. */
function showBootProblem(message, error) {
  if (error) console.error("GES boot:", error);
  let el = document.getElementById("boot-problem");
  if (!el) {
    el = document.createElement("div");
    el.id = "boot-problem";
    el.className = "boot-problem";
    document.body.appendChild(el);
  }
  el.innerHTML = `<div><b>Something went wrong</b><p>${esc(message)}</p>${error ? `<code>${esc(String(error.message || error))}</code>` : ""}
    <button class="primary" id="boot-retry">Retry</button></div>`;
  el.querySelector("#boot-retry").addEventListener("click", () => location.reload());
}

// ---------------------------------------------------------------- dashboard

async function enterDashboard() {
  $("dashboard-screen").classList.remove("hidden");
  await window.refreshAll();
  wireTabs();
  wireCommands();
  maybeShowFirstRunTour();
}

const TOUR_STOPS = [
  { title: "Your first move: launch a product", body: "Nothing earns until you sell something. Open Products and launch your first product: pick what it is, its specs and its budget, and it starts bringing in revenue at your first month close. Everything else (deals, offers from other companies, investors) follows once you have a product." },
  { title: "Welcome to the CEO Desk", body: "This is Overview — your command center. Priority Alerts up top surface the single most urgent thing across every system: Board, Risk, Competitors, Talent, FX, Investors. Start there each month." },
  { title: "Advance Time", body: "\"Next Month\" (top of the screen) closes the books, runs every engine, and gives you a Board Report. The \"Fast Forward\" dropdown next to it skips several months at once if you're in a quiet stretch — it'll still flag anything major that happened. Nothing advances time on its own; take as many actions as you want first." },
  { title: "Real, Interlocking Systems", body: "The tabs on the left aren't separate mini-games — they affect each other. A rival's price war (Competitors) can trigger a Board crisis (Board) that raises your next raise's dilution (Investors). Watch the Priority Alerts to catch it early." },
  { title: "Help Is One Click Away", body: "Every tab has a small (?) icon next to its title explaining what that section actually does. A red number on a tab means something there needs your answer: a letter, a payment due, an offer." },
];

let tourStopIndex = 0;

function maybeShowFirstRunTour() {
  if (localStorage.getItem("ges_tour_seen")) return;
  tourStopIndex = 0;
  renderTourStop();
  checkTourProducts();
}

function renderTourStop() {
  const existing = document.getElementById("tour-backdrop");
  if (existing) existing.remove();
  const stop = TOUR_STOPS[tourStopIndex];
  const backdrop = document.createElement("div");
  backdrop.id = "tour-backdrop";
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal tour-modal">
      <div class="tour-step-count">Step ${tourStopIndex + 1} of ${TOUR_STOPS.length}</div>
      <h3 class="tour-title">${esc(stop.title)}</h3>
      <p class="tour-body">${esc(stop.body)}</p>
      <div class="tour-actions">
        <button id="tour-skip" class="ghost">Skip tour</button>
        <button id="tour-next" class="primary">${tourStopIndex === TOUR_STOPS.length - 1 ? (tourNeedsProduct() ? "Launch my first product →" : "Get started →") : "Next →"}</button>
      </div>
    </div>
  `;
  document.body.appendChild(backdrop);
  $("tour-skip").addEventListener("click", endTour);
  $("tour-next").addEventListener("click", () => {
    if (tourStopIndex === TOUR_STOPS.length - 1) {
      const launch = tourNeedsProduct();
      endTour();
      if (launch) goLaunchFirstProduct();
      return;
    }
    tourStopIndex += 1;
    renderTourStop();
  });
}

/** A new company with nothing on sale yet: the tour ends in the launch flow (checked when the tour starts). */
let tourNoProducts = false;
function tourNeedsProduct() { return tourNoProducts; }
async function checkTourProducts() {
  try {
    const r = await fetch(`${API}/products`);
    const list = r.ok ? await r.json() : null;
    const rows = Array.isArray(list) ? list : (list && (list.products || list.items)) || [];
    tourNoProducts = Array.isArray(rows) && rows.length === 0;
  } catch (e) { tourNoProducts = false; }
  const next = document.getElementById("tour-next");
  if (next && tourStopIndex === TOUR_STOPS.length - 1 && tourNoProducts) next.textContent = "Launch my first product →";
}

function goLaunchFirstProduct() {
  showTab("products");
  if (typeof window.openLaunchProductModal === "function") setTimeout(() => window.openLaunchProductModal(), 300);
}

function endTour() {
  localStorage.setItem("ges_tour_seen", "1");
  const el = document.getElementById("tour-backdrop");
  if (el) el.remove();
}

// Cash spent by a command (launch, campaign, hire, loan…) leaves the ledger
// immediately; the month-end snapshot doesn't change until the next close.
// These read the live ledger balance from /api/state so the player sees
// the money go the moment they spend it.
function currentCash() {
  if (window.__live && typeof window.__live.cash === "number") return window.__live.cash;
  const snap = window.__snapshot;
  return (snap && snap.balance_sheet && snap.balance_sheet.CASH) || 0;
}

function cashSinceCloseNote() {
  const change = window.__live ? window.__live.cash_change_since_close : 0;
  if (!change || Math.abs(change) < 0.5) return "";
  return `<div class="cash-since-close ${change < 0 ? "neg" : "pos"}">${change < 0 ? "−" : "+"}${fmtMoney(Math.abs(change))} since month close</div>`;
}

/** Top-bar money: "$16.23B", not "$16,225,980,412" (on a phone the long form was cut to "$16,225,980…"). */
function kpiMoney(v) {
  const a = Math.abs(v || 0), s = v < 0 ? "−" : "";
  if (a >= 1e12) return `${s}$${(a / 1e12).toFixed(2)}T`;
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e7) return `${s}$${(a / 1e6).toFixed(1)}M`;
  return fmtMoney(v);
}

function renderCashKpi(cash, changeSinceClose) {
  const el = $("kpi-cash");
  if (!el) return;
  const prev = el.dataset.value !== undefined ? parseFloat(el.dataset.value) : null;
  el.textContent = kpiMoney(cash);
  el.dataset.value = String(cash);
  el.title = (changeSinceClose && Math.abs(changeSinceClose) >= 0.5
    ? `${changeSinceClose < 0 ? "Spent" : "Received"} ${fmtMoney(Math.abs(changeSinceClose))} since the last month close`
    : "Cash in the bank right now") + ` (${fmtMoney(cash)})`;
  if (prev !== null && Math.abs(prev - cash) >= 0.5) {
    el.classList.remove("cash-down", "cash-up");
    void el.offsetWidth; // restart the animation
    el.classList.add(cash < prev ? "cash-down" : "cash-up");
  }
}

// For scripts loaded after this one (ceo.js): top-level let/const aren't
// reachable from every loader, functions are.
function getCurrentTab() { return currentTab; }

/** Switch to a tab by id (also ones without a sidebar button, e.g. "agenda"). */
function showTab(tab) {
  // Offers for You is now a page inside the Investments & Offers tab (views/investhub.js)
  if (tab === "offers") { if (window.gesInvestPage) window.gesInvestPage("offers"); tab = "investments"; }
  // merged tabs (views/hub.js): "fx" and "news" are pages of World Economy, "league" of Rankings
  const hub = window.gesHubOf && window.gesHubOf(tab);
  if (hub && hub !== tab) { try { localStorage.setItem(`ges.hub.${hub}`, tab); } catch (e) { /* not remembered */ } tab = hub; }
  currentTab = tab;
  document.querySelectorAll(".sidebar .tab").forEach((b) => b.classList.toggle("active", b.dataset.tab === tab));
  return renderTab();
}

function wireTabs() {
  document.querySelectorAll(".sidebar .tab").forEach((btn) => {
    btn.addEventListener("click", () => {
      document.querySelectorAll(".sidebar .tab").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      const changed = currentTab !== btn.dataset.tab;
      currentTab = btn.dataset.tab;
      if (changed) { const m = $("main-content"); if (m) m.scrollTop = 0; }
      renderTab();
      window.syncMobileNav();
    });
  });
  buildMobileNav();
}

// ---------------------------------------------------------------- Mobile navigation
// On phones the sidebar is hidden (style.css, max-width 760px) and replaced
// by a bottom bar of tab groups; tapping a group opens a sheet with its tabs.
// Built from the sidebar itself so there is one list of tabs, and every tap
// goes through the sidebar button's own click handler.
const MOBILE_NAV_GROUPS = { "": ["Home", "🏠"], Finance: ["Money", "💰"], Growth: ["Growth", "📈"],
                            Market: ["Market", "🌐"], "People & Leadership": ["People", "👥"], "Your Life": ["Life", "💎"],
                            "World & League": ["World", "🏁"] };

function mobileNavGroups() {
  const groups = [];
  let current = { key: "", tabs: [] };
  for (const el of document.querySelectorAll(".sidebar > *")) {
    if (el.classList.contains("tab-group-label")) {
      if (current.tabs.length) groups.push(current);
      current = { key: el.textContent.trim(), tabs: [] };
    } else if (el.classList.contains("tab")) {
      current.tabs.push({ tab: el.dataset.tab, label: el.textContent.trim() });
    }
  }
  if (current.tabs.length) groups.push(current);
  return groups;
}

function buildMobileNav() {
  if (document.getElementById("mobile-nav")) return;
  const groups = mobileNavGroups();
  const nav = document.createElement("nav");
  nav.id = "mobile-nav";
  nav.className = "mobile-nav";
  nav.setAttribute("aria-label", "Sections");
  nav.innerHTML = groups.map((g, i) => {
    const [name, icon] = MOBILE_NAV_GROUPS[g.key] || [g.key, "•"];
    return `<button type="button" class="mobile-nav-btn" data-group="${i}"><span class="mobile-nav-icon" aria-hidden="true">${icon}</span><span>${esc(name)}</span></button>`;
  }).join("");
  const sheet = document.createElement("div");
  sheet.id = "mobile-sheet";
  sheet.className = "mobile-sheet hidden";
  sheet.setAttribute("role", "dialog");
  const dash = document.getElementById("dashboard-screen");
  if (!dash) return;
  dash.appendChild(sheet);
  dash.appendChild(nav);

  const closeSheet = () => sheet.classList.add("hidden");
  nav.querySelectorAll(".mobile-nav-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const g = groups[parseInt(btn.dataset.group, 10)];
      if (g.tabs.length === 1) { openTabFromMobile(g.tabs[0].tab); closeSheet(); return; }
      const [name] = MOBILE_NAV_GROUPS[g.key] || [g.key];
      sheet.innerHTML = `
        <div class="mobile-sheet-panel">
          <div class="mobile-sheet-title">${esc(name)}</div>
          ${g.tabs.map((t) => `<button type="button" class="mobile-sheet-item${t.tab === currentTab ? " active" : ""}" data-tab="${esc(t.tab)}">${esc(t.label)}</button>`).join("")}
        </div>`;
      sheet.classList.remove("hidden");
      sheet.querySelectorAll(".mobile-sheet-item").forEach((item) => {
        item.addEventListener("click", () => { openTabFromMobile(item.dataset.tab); closeSheet(); });
      });
    });
  });
  sheet.addEventListener("click", (e) => { if (e.target === sheet) closeSheet(); });
  window.syncMobileNav();
}

function openTabFromMobile(tab) {
  const btn = document.querySelector(`.sidebar .tab[data-tab="${tab}"]`);
  if (btn) btn.click();
  const main = document.getElementById("main-content");
  if (main) main.scrollTop = 0;
  window.scrollTo(0, 0);
}

function syncMobileNav() {
  const nav = document.getElementById("mobile-nav");
  if (!nav) return;
  const groups = mobileNavGroups();
  nav.querySelectorAll(".mobile-nav-btn").forEach((btn) => {
    const g = groups[parseInt(btn.dataset.group, 10)];
    btn.classList.toggle("active", !!g && g.tabs.some((t) => t.tab === currentTab));
  });
}

async function refreshAll() {
  // start every read this tab's refresh will make, together, now (api.js gesPrefetchRefresh)
  if (window.gesPrefetchRefresh) window.gesPrefetchRefresh(currentTab);
  const mainEl = document.getElementById("main-content");
  const scrollTop = mainEl ? mainEl.scrollTop : 0;

  const [companyRes, stateRes, snapsRes, logRes] = await Promise.all([
    fetch(`${API}/company`), fetch(`${API}/state`), fetch(`${API}/snapshots`), fetch(`${API}/audit-log?limit=60`),
  ]);
  const company = await companyRes.json();
  const state = await stateRes.json();
  snapshotHistory = await snapsRes.json();
  const log = await logRes.json();

  window.__company = company;
  window.__snapshot = state.snapshot;
  window.__live = state.live || null;
  // the credit rating is a "right now" grade: every screen shows the live one (engine.live_position)
  if (state.live && state.live.credit_rating && state.snapshot.ratios) {
    state.snapshot.ratios.credit_rating = state.live.credit_rating;
    if (state.live.interest_coverage !== null && state.live.interest_coverage !== undefined) state.snapshot.ratios.interest_coverage = state.live.interest_coverage;
    state.snapshot.ratios.debt_to_equity = state.live.debt_to_equity;
  }

  if (company.game_over) {
    showGameOver(company, state.snapshot);
    return;
  }

  $("db-company-name").textContent = company.name;
  mountSoundControl(document.getElementById("btn-sound"));
  setAmbientGroup(company.world_group || "digital");
  $("db-company-meta").textContent = `${company.industry} · ${company.hq_country} · ${company.capital_tier}`;
  $("kpi-month").textContent = state.snapshot.month;
  renderCashKpi(currentCash(), state.live ? state.live.cash_change_since_close : 0);
  $("kpi-credit").textContent = (state.snapshot.ratios && state.snapshot.ratios.credit_rating) || "—";
  const vr = state.snapshot.ratios || {};
  $("kpi-valuation").textContent = vr.valuation_status === "not_valued" ? "Not valued yet" : kpiMoney(vr.valuation);
  $("kpi-valuation").title = vr.valuation_status === "not_valued" ? "" : fmtMoney(vr.valuation);
  const vb = document.getElementById("kpi-valuation-btn");
  if (vb && !vb.dataset.wired) { vb.dataset.wired = "1"; vb.addEventListener("click", () => window.openValuationExplainer && window.openValuationExplainer()); }
  $("kpi-brand").textContent = (state.snapshot.ratios && state.snapshot.ratios.brand_score) || "50";

  renderTicker(log);
  await renderTab();
  checkNewAchievements();

  // Restore scroll position — without this, every single action (market a
  // product, sign a deal, anything) yanks the view back to the top of the
  // tab, which makes long lists (Operations, Deals) unusable.
  const newMainEl = document.getElementById("main-content");
  if (newMainEl) newMainEl.scrollTop = scrollTop;
}

let knownUnlockedAchievements = null;

async function checkNewAchievements() {
  try {
    const achievements = await fetch(`${API}/achievements`).then((x) => x.json());
    const unlockedIds = new Set(achievements.filter((a) => a.unlocked).map((a) => a.id));
    if (knownUnlockedAchievements === null) {
      knownUnlockedAchievements = unlockedIds; // first load — don't celebrate pre-existing progress
      return;
    }
    const newlyUnlocked = achievements.filter((a) => a.unlocked && !knownUnlockedAchievements.has(a.id));
    knownUnlockedAchievements = unlockedIds;
    newlyUnlocked.forEach((a) => toast(`🏆 Achievement unlocked: ${a.name}`));
  } catch (e) { /* achievements are a nice-to-have — never block the game on this */ }
}

function renderTicker(log) {
  const track = $("ticker-track");
  if (!log.length) {
    track.innerHTML = `<span class="ticker-item">Awaiting first month close…</span>`;
    track.style.animationDuration = "40s";
    return;
  }
  // only real news (2.7): housekeeping events ("decision raised", journal lines) and events with no sentence of their
  // own stay out, and the same line never shows twice
  const seen = new Set();
  const lines = log.map((e) => tickerLine(e)).filter((l) => l && !seen.has(l.replace(/<span class="t-month">M\d+<\/span>/, "")) && seen.add(l.replace(/<span class="t-month">M\d+<\/span>/, "")));
  if (!lines.length) {
    track.innerHTML = `<span class="ticker-item">No big news this month.</span>`;
    track.style.animationDuration = "40s";
    return;
  }
  const items = lines.join("");
  track.innerHTML = items + items;

  // Speed must stay constant regardless of how long the log grows — a fixed
  // duration on a growing track was making the ticker race by as the game
  // went on. 28px/sec is a comfortable, unhurried reading pace; the track is
  // doubled for the seamless loop, so only half its width is actually travelled.
  const halfWidth = track.scrollWidth / 2;
  const pxPerSecond = 28;
  const duration = Math.max(20, halfWidth / pxPerSecond);
  track.style.animationDuration = `${duration}s`;
}

async function showGameOver(company, snapshot) {
  $("dashboard-screen").classList.add("hidden");
  $("onboarding-screen").classList.add("hidden");
  $("gameover-screen").classList.remove("hidden");
  $("gameover-title").textContent = `${company.name} — Run Ended`;
  $("gameover-reason").textContent = company.game_over_reason || "The game has ended.";
  $("gameover-summary").innerHTML = `
    <div class="gs-item"><div class="gs-label">Months Survived</div><div class="gs-value">${snapshot.month}</div></div>
    <div class="gs-item"><div class="gs-label">Difficulty</div><div class="gs-value">${esc(company.difficulty)}</div></div>
    <div class="gs-item"><div class="gs-label">Final Cash</div><div class="gs-value">${fmtMoney(snapshot.balance_sheet.CASH)}</div></div>
    <div class="gs-item"><div class="gs-label">Peak Valuation</div><div class="gs-value">${fmtMoney(snapshot.ratios && snapshot.ratios.valuation)}</div></div>
  `;
  $("gameover-new-game").onclick = () => startNewGame(null);
  try {
    const achievements = await fetch(`${API}/achievements`).then((x) => x.json());
    const unlocked = achievements.filter((a) => a.unlocked);
    const list = $("gameover-achievements");
    if (list) {
      list.innerHTML = unlocked.length
        ? `<h3 style="font-family:var(--font-display); font-size:15px; margin:24px 0 10px; color:var(--text-hi);">Achievements Earned (${unlocked.length}/${achievements.length})</h3>
           <div class="achievements-strip">${unlocked.map((a) => renderAchievementBadge(a)).join("")}</div>`
        : "";
    }
  } catch (e) { /* epilogue still shows without achievements if this fails */ }
  // an exit's story, the next company, the time machine (views/career.js, late-bound)
  if (window.renderGameOverExtras) window.renderGameOverExtras(company, snapshot).catch(() => {});
}

const ACHIEVEMENT_TIER_EMOJI = { bronze: "🥉", silver: "🥈", gold: "🥇" };

function renderAchievementBadge(a) {
  if (a.tiers) {
    const emoji = a.tier_reached ? ACHIEVEMENT_TIER_EMOJI[a.tier_reached] : "🔒";
    const label = a.tier_reached ? a.label : a.name;
    const progressNote = a.next_threshold != null ? ` — ${a.progress_pct}% to next tier` : " — maxed out";
    return `
      <span class="achievement-badge tiered ${a.tier_reached ? "unlocked " + a.tier_reached : ""}" title="${esc(a.description)}${esc(progressNote)}">
        ${emoji} ${esc(label)}
      </span>`;
  }
  return `
    <span class="achievement-badge ${a.unlocked ? "unlocked" : ""}" title="${esc(a.description)}">
      ${a.unlocked ? "🏆 " : "🔒 "}${esc(a.name)}
    </span>`;
}

function rivalryTag(clashNumber) {
  if (!clashNumber || clashNumber < 2) return "";
  const ordinal = { 2: "2nd", 3: "3rd", 4: "4th", 5: "5th" }[clashNumber] || `${clashNumber}th`;
  return ` 🔁 (${ordinal} clash)`;
}

// bookkeeping, not news: these used to scroll by as "M14 decision raised" again and again
const TICKER_SKIP = new Set(["decision_raised", "decision_answered", "journal_posted", "budget_variance", "voluntary_attrition",
  "drive", "forecast", "read_cache", "tab_opened", "prefetch"]);

function tickerLine(e) {
  let text = "";
  switch (e.type) {
    case "company_founded": text = `${e.payload.name} founded — ${e.payload.funding_type}, ${e.payload.capital_tier}`; break;
    case "month_closed": text = `Month ${e.payload.month} closed — Net income ${fmtMoney(e.payload.net_income)}`; break;
    case "capital_raised": text = `Equity capital raised: ${fmtMoney(e.payload.amount)}`; break;
    case "loan_taken": text = `Loan drawn: ${fmtMoney(e.payload.amount)}`; break;
    case "debt_repaid": text = `Debt repaid: ${fmtMoney(e.payload.amount)}`; break;
    case "dividend_paid": text = `Dividend paid: ${fmtMoney(e.payload.amount)}`; break;
    case "capex_purchased": text = `Capital expenditure: ${fmtMoney(e.payload.amount)}`; break;
    case "marketing_campaign": text = `Marketing campaign: ${fmtMoney(e.payload.amount)}`; break;
    case "rnd_investment": text = `R&D investment: ${fmtMoney(e.payload.amount)}`; break;
    case "market_expansion": text = `Market expansion: ${fmtMoney(e.payload.amount)}`; break;
    case "executive_hired": text = `${e.payload.name} hired as ${e.payload.role}`; break;
    case "executive_departed": text = `${e.payload.name} (${e.payload.role}) departed`; break;
    case "cost_cutting_set": text = `Cost-cutting set to ${Math.round(e.payload.pct * 100)}%`; break;
    case "product_launched": text = `Product launched: ${e.payload.name} (${e.payload.category})`; break;
    case "product_marketing": text = `Marketing push for ${e.payload.product}: ${fmtMoney(e.payload.amount)}`; break;
    case "product_retired": text = `Product retired: ${e.payload.name}`; break;
    case "deal_proposed": text = `${e.payload.company_name} proposed a ${e.payload.deal_type} — est. ARR ${fmtMoney(e.payload.arr_low)}-${fmtMoney(e.payload.arr_high)}`; break;
    case "deal_signed": text = `${e.payload.company_name} deal ${e.payload.full_success ? "SIGNED in full" : "signed on reduced terms"}`; break;
    case "deal_rejected": text = `Walked away from ${e.payload.company_name}`; break;
    case "competitor_action": text = `${e.payload.description}`; break;
    case "new_competitor_entrant": text = `New competitor enters the market: ${e.payload.name}`; break;
    case "competitive_response": text = `Responded to ${e.payload.competitor} — ${e.payload.response}${e.payload.cost ? ` (${fmtMoney(e.payload.cost)})` : ""}${rivalryTag(e.payload.clash_number)}`; break;
    case "competitor_acquired": text = `🏳 Acquired rival ${e.payload.target} for ${fmtMoney(e.payload.price)}${rivalryTag(e.payload.clash_number)}`; break;
    case "risk_incident": text = `⚠ ${e.payload.description}`; break;
    case "insurance_purchased": text = `Bound ${e.payload.policy_type} coverage up to ${fmtMoney(e.payload.coverage_limit)}`; break;
    case "insurance_cancelled": text = `Cancelled ${e.payload.policy_type} coverage`; break;
    case "compliance_audit": text = `Ran a ${fmtMoney(e.payload.amount)} compliance audit (+${e.payload.compliance_gain} score)`; break;
    case "cyber_defense_investment": text = `Invested ${fmtMoney(e.payload.amount)} in cyber defense (+${e.payload.cyber_defense_gain})`; break;
    case "board_meeting": text = `Board reviewed performance — confidence ${e.payload.confidence}/100 (${e.payload.verdict})`; break;
    case "board_intervention": text = `⚠ Board mandates cost discipline — cost cuts set to ${(e.payload.cost_cut_pct*100).toFixed(0)}%`; break;
    case "board_vote_no_confidence": text = `⚠ Board votes no confidence — forced ${fmtMoney(e.payload.forced_dividend)} emergency dividend`; break;
    case "board_addressed": text = `Addressed the Board on ${e.payload.focus} (+${e.payload.confidence_gain} confidence)`; break;
    case "ipo_completed": text = `🔔 IPO complete — raised ${fmtMoney(e.payload.primary_raise)}`; break;
    case "fx_hedge_purchased": text = `Locked ${e.payload.currency} forward hedge for ${e.payload.months}mo (${fmtMoney(e.payload.premium)} premium)`; break;
    case "fx_translation": text = `FX translation this month: ${e.payload.total_gain_loss_usd > 0 ? "+" : ""}${fmtMoney(e.payload.total_gain_loss_usd)}`; break;
    case "esop_pool_created": text = `Carved out a ${e.payload.pct}% ESOP pool`; break;
    case "investor_call": text = `Held an investor call (+${e.payload.sentiment_gain} sentiment)`; break;
    case "stock_buyback": text = `Bought back ${fmtMoney(e.payload.amount)} of stock (~${e.payload.pct_bought_back}%)`; break;
    case "executive_poached": text = `⚠ ${e.payload.name} (${e.payload.role}) poached by ${e.payload.poached_by}${rivalryTag(e.payload.clash_number)}`; break;
    case "executive_resigned": text = `${e.payload.name} (${e.payload.role}) resigned`; break;
    case "executive_auto_hired": text = `${e.payload.name} joins as ${e.payload.role} (${fmtMoney(e.payload.annual_salary)}/yr) — the company grew into needing the seat`; break;
    case "factory_auto_expanded": text = `Auto-expanded capacity for ${e.payload.product}: +${e.payload.additional_capacity.toLocaleString("en-US")} units (${fmtMoney(e.payload.investment)}) — was at ${e.payload.utilization_pct_before}% utilization`; break;
    case "activist_investor_pressure": text = `⚠ Activist investor pressure after ${e.payload.consecutive_misses} missed quarters — forced buyback of ${fmtMoney(e.payload.forced_buyback)}`; break;
    // the newer systems write their own sentence; an event without one isn't news, so it stays off the ticker
    default: {
      if (TICKER_SKIP.has(e.type)) return "";
      const own = e.payload && (e.payload.description || e.payload.text);
      if (!own) return "";
      text = esc(own);
    }
  }
  return `<span class="ticker-item"><span class="t-month">M${e.month}</span>${text}</span>`;
}

// ---------------------------------------------------------------- tab rendering

let renderSeq = 0;

/**
 * Draw the current tab. Screens load their data asynchronously, so the
 * player can switch tabs (or a refresh can redraw) before an older render
 * finishes. Each render therefore gets its own container inside
 * #main-content:
 * - the new container goes first; the previous one stays on screen until
 *   the new render writes something, so there's no blank flash;
 * - a stale render keeps writing into its own, now detached, container,
 *   so it can never paint over the screen you switched to;
 * - an error from a superseded render is dropped (nobody is looking at
 *   that screen); an error in the current render propagates.
 * #main-content stays the scroll container.
 */
async function renderTab() {
  const snap = window.__snapshot;
  const host = $("main-content");
  if (!snap) { host.innerHTML = ""; return; }
  const tab = currentTab, seq = ++renderSeq;
  // a view = the tab's 3D home (homes/index.js, Phase 6) + the tab's own content
  const view = document.createElement("div");
  view.className = "tab-view";
  view.dataset.tab = tab;
  const homeEl = document.createElement("section");
  homeEl.className = "tab-home";
  const body = document.createElement("div");
  body.className = "tab-body";
  view.append(homeEl, body);
  const previous = [...host.childNodes];
  host.prepend(view);
  const retire = () => previous.forEach((n) => { if (n.__home) n.__home.dispose(); n.remove(); });
  const swap = new MutationObserver(() => { swap.disconnect(); retire(); });
  swap.observe(body, { childList: true });
  view.__home = mountHome(homeEl, tab, snap, body);
  if (!view.__home) homeEl.remove();
  try {
    const out = await dispatchTab(body, snap);
    // release 1.7: extra panels other modules add to a tab (views/empire.js, late-bound)
    if (window.gesTabExtras && tab === currentTab && seq === renderSeq) await window.gesTabExtras(tab, body, snap).catch((e) => console.warn("tab extras:", e));
    return out;
  } catch (e) {
    if (tab !== currentTab || seq !== renderSeq) return; // superseded: stale render
    // never a blank page: say what happened and offer to try again
    console.warn(`tab ${tab}:`, e);
    body.innerHTML = `<div class="tab-retry" role="alert"><b>This page didn't load.</b>
      <p>${esc((e && e.message) || "Something went wrong.")}</p>
      <button type="button" class="primary" data-tab-retry>Try again</button></div>`;
    body.querySelector("[data-tab-retry]").addEventListener("click", () => renderTab());
  } finally {
    swap.disconnect();
    if (seq === renderSeq) retire();
  }
}

window.renderMacroCore = renderMacro;
window.renderFXCore = renderFX;
window.renderNewsCore = renderNews;

function dispatchTab(main, snap) {
  if (currentTab === "overview") return window.renderOverview(main, snap);
  if (currentTab === "financials") return renderFinancials(main, snap);
  if (currentTab === "ratios") return renderRatios(main, snap);
  if (currentTab === "banking") return window.renderBanking(main, snap);
  if (currentTab === "publicmarkets") return renderPublicMarkets(main, snap);
  if (currentTab === "products") return window.renderProducts(main, snap);
  if (currentTab === "operations") return window.renderOperations(main, snap);
  if (currentTab === "deals") return window.renderDeals(main, snap);
  if (currentTab === "ma") return window.renderMA(main, snap);
  if (currentTab === "competitors") return renderCompetitors(main, snap);
  if (currentTab === "risk") return renderRisk(main, snap);
  if (currentTab === "board") return window.renderBoard(main, snap);
  if (currentTab === "investors") return window.renderInvestors(main, snap);
  // World Economy: Economy, Currencies and News in one tab; Rankings: real companies and players (views/hub.js)
  if (currentTab === "macro" || currentTab === "fx" || currentTab === "news") return window.renderHub("macro", main, snap, currentTab);
  if (currentTab === "government") return window.renderGovernment(main, snap);
  if (currentTab === "countries") return window.renderCountries(main, snap); // views/countries.js
  if (currentTab === "footprint") return renderGlobalFootprint(main, snap);
  if (currentTab === "executives" || currentTab === "talent") return window.renderExecutives(main, snap);
  if (currentTab === "consulting") return renderConsulting(main, snap);
  if (currentTab === "ranking" || currentTab === "league") return window.renderHub("ranking", main, snap, currentTab);
  if (currentTab === "office") return window.renderOffice(main, snap); // views/office.js: Inbox, Agenda, Goals, Delegation
  if (currentTab === "agenda") return window.renderAgenda(main, snap); // views/agenda.js (also inside the CEO Office)
  if (currentTab === "consequences") return window.renderConsequences(main, snap); // views/consequences.js
  // one tab, two pages: Corporate Investments and Offers for You (views/investhub.js)
  if (currentTab === "investments" || currentTab === "offers") return window.renderInvestHub(main, snap);
  if (currentTab === "world") return window.renderWorld(main, snap); // views/world.js (3D, lazy)
  // release 1.6, the empire systems (backend/systems.py)
  if (currentTab === "workforce") return window.renderWorkforce(main, snap); // views/workforce.js
  if (currentTab === "esg") return window.renderEsg(main, snap); // views/esg.js
  if (currentTab === "rnd") return window.renderRnd(main, snap); // views/rnd.js
  if (currentTab === "ambassadors" || currentTab === "marketing") return window.renderMarketing(main, snap); // views/marketing.js (+ ambassadors)
  // 2026-10-22: your personal life, and owning a sports team
  if (currentTab === "lifestyle") return window.renderLifestyle(main, snap); // views/lifestyle.js
  if (currentTab === "dream") return window.renderDream(main, snap); // views/dream.js
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.refreshAll = refreshAll;
window.syncMobileNav = syncMobileNav;

export { boot, cashSinceCloseNote, currentCash, enterDashboard, getCurrentTab, mobileNavGroups, optionsCache, renderAchievementBadge, renderTab, showTab, snapshotHistory };
